import React from "react";
import { createPortal } from "react-dom";

/*
 * 이메일 설정의 메일 계정 다이얼로그 공용 부품.
 * 기본 계정(주문 메일함)과 추가 발신 계정이 같은 메일 서비스 선택·앱 비밀번호 안내를 쓴다.
 */

/** 화면의 메일 서비스 값. 백엔드 MailServiceProvider 와 이름이 다르다(KAKAO=DAUM, GMAIL=GOOGLE, OTHERS=OTHER) */
export type FormMailProvider = "NAVER" | "KAKAO" | "GMAIL" | "OTHERS";

// 브랜드 컬러 상수
export const BRAND_COLOR = "#4285F4";
export const BRAND_BG_COLOR = "#E8EFFF";

// 각 플랫폼별 앱 비밀번호 발급 안내 데이터 (브랜드 컬러 통일)
export const appPasswordGuides: Record<
  "NAVER" | "KAKAO" | "GMAIL",
  {
    title: string;
    steps: { title: string; description: string; link?: { url: string; text: string } }[];
  }
> = {
  GMAIL: {
    title: "Gmail 앱 비밀번호 발급 방법",
    steps: [
      {
        title: "1. 구글 계정 관리로 이동하세요.",
        description: "로 이동하여 로그인합니다.",
        link: { url: "https://myaccount.google.com/", text: "구글 계정 관리" },
      },
      {
        title: "2. 2단계 인증을 설정하세요.",
        description: "보안 및 로그인 > 2단계 인증으로 이동하여 2단계 인증을 완료합니다.",
      },
      {
        title: "3. 앱 비밀번호를 생성하세요.",
        description: "2단계 인증 설정 후, 앱 비밀번호 메뉴에서 알아보기 쉬운 이름을 입력하고 '만들기'를 클릭합니다.",
      },
      {
        title: "4. 생성된 비밀번호를 입력하세요.",
        description: "생성된 16자리 비밀번호를 복사하여 아래 입력란에 붙여넣으세요.",
      },
    ],
  },
  NAVER: {
    title: "네이버 메일 앱 비밀번호 발급 방법",
    steps: [
      {
        title: "1. 네이버 메일 환경설정으로 이동하세요.",
        description: "에서 좌측 하단의 '환경설정'을 클릭합니다.",
        link: { url: "https://mail.naver.com/", text: "네이버 메일" },
      },
      {
        title: "2. IMAP/SMTP 설정을 활성화하세요.",
        description: "POP3/IMAP 설정 > IMAP/SMTP 설정 탭에서 '설정하기' 버튼을 클릭합니다.",
      },
      {
        title: "3. 2단계 인증을 설정하세요.",
        description: "'2단계 인증' 옆의 '관리' 버튼을 클릭하여 2단계 인증을 활성화합니다.",
      },
      {
        title: "4. 애플리케이션 비밀번호를 생성하세요.",
        description: "애플리케이션 비밀번호 관리에서 '생성하기'를 클릭하고, 생성된 비밀번호를 복사하여 입력하세요.",
      },
    ],
  },
  KAKAO: {
    title: "카카오(다음) 메일 앱 비밀번호 발급 방법",
    steps: [
      {
        title: "1. 다음 메일 설정으로 이동하세요.",
        description: "에서 좌측 하단의 '설정'을 클릭합니다.",
        link: { url: "https://mail.daum.net/", text: "다음 메일" },
      },
      {
        title: "2. IMAP/POP3 설정으로 이동하세요.",
        description: "설정 메뉴에서 'IMAP/POP3' 탭을 선택합니다.",
      },
      {
        title: "3. 2단계 인증을 설정하세요.",
        description: "2단계 인증이 필요하다는 안내가 표시되면, '2단계 인증 설정하기'를 클릭하여 인증을 완료합니다.",
      },
      {
        title: "4. 앱 비밀번호를 생성하세요.",
        description: "앱 이름에 알아보기 쉬운 이름을 입력하고 '생성'을 클릭합니다. 생성된 비밀번호를 복사하여 입력하세요.",
      },
    ],
  },
};

// 플랫폼별 안내 메시지 (브랜드 컬러 통일)
export const providerMessages: Record<FormMailProvider, string> = {
  GMAIL: "Gmail을 사용합니다. 앱 비밀번호가 필요합니다.",
  NAVER: "네이버 메일을 사용합니다. 애플리케이션 비밀번호가 필요합니다.",
  KAKAO: "카카오(다음) 메일을 사용합니다. 앱 비밀번호가 필요합니다.",
  OTHERS: "기타 메일 서비스를 사용합니다. IMAP 설정이 필요할 수 있습니다.",
};

// 메일 서비스 아이콘 (토스페이 스타일)
export const MailProviderIcon: React.FC<{ provider: FormMailProvider; size?: number }> = ({
  provider,
  size = 32,
}) => {
  switch (provider) {
    case "NAVER":
      return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect width="32" height="32" rx="8" fill="#03C75A" />
          <path d="M18.5 16.8L13.3 9H9V23H13.5V15.2L18.7 23H23V9H18.5V16.8Z" fill="white" />
        </svg>
      );
    case "KAKAO":
      return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect width="32" height="32" rx="8" fill="#FEE500" />
          <path
            d="M16 8C11.029 8 7 11.164 7 15.079C7 17.557 8.649 19.74 11.12 21.022L10.203 24.532C10.152 24.732 10.376 24.894 10.552 24.779L14.715 22.024C15.133 22.072 15.562 22.099 16 22.099C20.971 22.099 25 18.935 25 15.02C25 11.164 20.971 8 16 8Z"
            fill="#391B1B"
          />
        </svg>
      );
    case "GMAIL":
      return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect width="32" height="32" rx="8" fill="white" />
          <rect x="0.5" y="0.5" width="31" height="31" rx="7.5" stroke="#E5E7EB" />
          {/* Gmail 2020 공식 아이콘 */}
          <svg x="4" y="5" width="24" height="22" viewBox="52 42 88 66">
            <path fill="#4285f4" d="M58 108h14V74L52 59v43c0 3.32 2.69 6 6 6"/>
            <path fill="#34a853" d="M120 108h14c3.32 0 6-2.69 6-6V59l-20 15"/>
            <path fill="#fbbc04" d="M120 48v26l20-15v-8c0-7.42-8.47-11.65-14.4-7.2"/>
            <path fill="#ea4335" d="M72 74V48l24 18 24-18v26L96 92"/>
            <path fill="#c5221f" d="M52 51v8l20 15V48l-5.6-4.2c-5.94-4.45-14.4-.22-14.4 7.2"/>
          </svg>
        </svg>
      );
    case "OTHERS":
      return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect width="32" height="32" rx="8" fill="#F3F4F6" />
          <rect x="0.5" y="0.5" width="31" height="31" rx="7.5" stroke="#E5E7EB" />
          <path
            d="M7 11C7 10.4477 7.44772 10 8 10H24C24.5523 10 25 10.4477 25 11V21C25 21.5523 24.5523 22 24 22H8C7.44772 22 7 21.5523 7 21V11Z"
            stroke="#6B7280"
            strokeWidth="1.5"
          />
          <path d="M7 11L16 17L25 11" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
  }
};

// 메일 서비스 옵션 데이터
export const mailProviderOptions: { value: FormMailProvider; name: string }[] = [
  { value: "NAVER", name: "네이버" },
  { value: "KAKAO", name: "카카오" },
  { value: "GMAIL", name: "Gmail" },
  { value: "OTHERS", name: "기타" },
];


/** 메일 서비스 선택 카드 */
export const ProviderPicker: React.FC<{
  value: FormMailProvider;
  onChange: (value: FormMailProvider) => void;
  /** 고를 수 있는 서비스. 보내기 전용 계정은 SMTP 서버를 알 수 없는 '기타'를 뺀다 */
  options?: FormMailProvider[];
}> = ({ value, onChange, options }) => {
  const visible = mailProviderOptions.filter((option) => !options || options.includes(option.value));
  return (
    <div className={`grid gap-3 ${visible.length === 4 ? "grid-cols-4" : "grid-cols-3"}`}>
      {visible.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          aria-pressed={value === option.value}
          className={`relative flex flex-col items-center justify-center gap-2 p-4 rounded-[12px] border-2 transition-all ${
            value === option.value
              ? "border-[#4285F4] bg-[#F8FAFF]"
              : "border-[#E5E7EB] bg-white hover:border-[#D1D5DB] hover:bg-[#F9FAFB]"
          }`}
        >
          {value === option.value && (
            <div className="absolute top-2 right-2">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="9" cy="9" r="9" fill="#4285F4" />
                <path d="M5.5 9L8 11.5L12.5 6.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          )}
          <MailProviderIcon provider={option.value} size={36} />
          <span className={`text-[13px] font-medium ${value === option.value ? "text-[#4285F4]" : "text-[#374151]"}`}>
            {option.name}
          </span>
        </button>
      ))}
    </div>
  );
};

/** 메일 서비스 아래 안내 문구 */
export const ProviderNotice: React.FC<{ provider: FormMailProvider }> = ({ provider }) => (
  <div className="flex gap-2 items-start px-4 py-3 rounded-[8px] mt-3" style={{ backgroundColor: BRAND_BG_COLOR }}>
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0 mt-0.5">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18ZM9 7C9 6.44772 9.44772 6 10 6C10.5523 6 11 6.44772 11 7C11 7.55228 10.5523 8 10 8C9.44772 8 9 7.55228 9 7ZM10 9C9.44772 9 9 9.44772 9 10V13C9 13.5523 9.44772 14 10 14C10.5523 14 11 13.5523 11 13V10C11 9.44772 10.5523 9 10 9Z"
        fill={BRAND_COLOR}
      />
    </svg>
    <p className="text-[13px] leading-[1.4]" style={{ color: BRAND_COLOR }}>{providerMessages[provider]}</p>
  </div>
);

/** '발급 방법 안내' 버튼 */
export const GuideButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="text-[12px] text-[#4285F4] bg-white border border-[#4285F4] hover:bg-[#4285F4] hover:text-white px-3 py-1.5 rounded-[6px] flex items-center gap-1.5 font-medium transition-all shadow-sm"
  >
    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18ZM10 6C9.44772 6 9 6.44772 9 7C9 7.55228 9.44772 8 10 8C10.5523 8 11 7.55228 11 7C11 6.44772 10.5523 6 10 6ZM9 10C9 9.44772 9.44772 9 10 9C10.5523 9 11 9.44772 11 10V14C11 14.5523 10.5523 15 10 15C9.44772 15 9 14.5523 9 14V10Z"
        fill="currentColor"
      />
    </svg>
    발급 방법 안내
  </button>
);

/** 앱 비밀번호 발급 방법 다이얼로그 (계정 다이얼로그 위에 뜬다) */
export const AppPasswordGuideDialog: React.FC<{ provider: Exclude<FormMailProvider, "OTHERS">; onClose: () => void }> = ({ provider, onClose }) => {
  const guide = appPasswordGuides[provider];
  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center z-[10001]">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-[16px] w-[440px] max-w-[90vw] shadow-2xl overflow-hidden text-left">
        <div className="px-6 py-5 border-b border-[#E5E7EB] flex items-center justify-between">
          <h2 className="text-[17px] font-bold text-[#222]">{guide.title}</h2>
          <button type="button" onClick={onClose} aria-label="닫기" className="text-[#9CA3AF] hover:text-[#6B7280] transition-colors p-1 -mr-1">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
        <div className="px-6 py-5 flex flex-col gap-4">
          {guide.steps.map((step, index) => (
            <div key={index} className="flex gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[12px] font-bold text-white" style={{ backgroundColor: BRAND_COLOR }}>
                {index + 1}
              </div>
              <div className="flex-1 pt-0.5">
                <p className="text-[14px] font-semibold text-[#333] mb-1">{step.title.replace(/^\d+\.\s*/, "")}</p>
                <p className="text-[13px] text-[#666] leading-[1.6]">
                  {step.link ? (
                    <>
                      <a href={step.link.url} target="_blank" rel="noopener noreferrer" className="text-[#4285F4] font-medium hover:underline">
                        {step.link.text}
                      </a>
                      {step.description}
                    </>
                  ) : (
                    step.description
                  )}
                </p>
              </div>
            </div>
          ))}
        </div>
        <div className="px-6 py-4 border-t border-[#E5E7EB]">
          <button type="button" onClick={onClose} className="w-full text-white text-[14px] font-semibold py-3 rounded-[8px] hover:opacity-90 transition-opacity" style={{ backgroundColor: BRAND_COLOR }}>
            확인했습니다
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

/** 메일 계정 추가·수정 다이얼로그 틀 */
export const MailAccountDialog: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({ title, onClose, children }) =>
  createPortal(
    <div className="fixed inset-0 flex items-center justify-center z-[10000]">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div role="dialog" aria-modal="true" aria-label={title} className="relative bg-white rounded-[12px] w-[560px] max-w-[92vw] max-h-[90vh] overflow-y-auto shadow-2xl p-6 text-left">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-[17px] font-bold text-[#222]">{title}</h2>
          <button type="button" onClick={onClose} aria-label="닫기" className="w-[28px] h-[28px] flex items-center justify-center rounded-full hover:bg-[#F5F5F5] text-[#888]">
            ×
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
